import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Seo } from "../components/Layout";
import api from "../lib/api";

const BASE = "https://www.parfaitsimparfaits.fr";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPost(null);
    setError(null);
    api.get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | Parfaits Imparfaits`;
      })
      .catch(() => setError("Article introuvable."));
    api.get("/blog").then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 2))).catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <section className="pt-40 pb-32 text-center">
        <Seo title="Article introuvable" />
        <p className="text-sand-700">{error}</p>
        <Link to="/blog" data-testid="back-to-blog" className="btn-outline mt-8">
          Retour au blog
        </Link>
      </section>
    );
  }

  if (!post) {
    return (
      <section className="pt-40 pb-32 text-center" data-testid="post-loading">
        <p className="text-sand-600">Chargement…</p>
      </section>
    );
  }

  const canonical = `${BASE}/blog/${post.slug}`;
  const imageUrl = post.cover_image?.startsWith("http") ? post.cover_image : `${BASE}${post.cover_image}`;

  return (
    <>
      <Seo
        title={post.title}
        description={post.excerpt}
        image={imageUrl}
        url={canonical}
        canonical={canonical}
      />

      {/* Schema.org Article + BreadcrumbList */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: imageUrl,
            datePublished: post.created_at,
            dateModified: post.updated_at,
            author: { "@type": "Person", name: "Valentine DL", url: `${BASE}/qui-suis-je` },
            publisher: {
              "@type": "Organization",
              name: "Parfaits Imparfaits",
              logo: { "@type": "ImageObject", url: `${BASE}/img/logo-dark.webp` },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
          }),
        }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${BASE}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: canonical },
            ],
          }),
        }}
      />

      <article className="pt-32 pb-32" data-testid="blog-article">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {/* Breadcrumb visuel */}
          <nav aria-label="Fil d'Ariane" className="flex items-center gap-2 text-sm text-sand-600 mb-8 flex-wrap" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-sand-900">Accueil</Link>
            <ChevronRight size={14} className="text-sand-400" />
            <Link to="/blog" className="hover:text-sand-900">Blog</Link>
            <ChevronRight size={14} className="text-sand-400" />
            <span className="text-sand-800 line-clamp-1">{post.title}</span>
          </nav>

          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sand-600 hover:text-sand-900 mb-6 text-sm">
            <ArrowLeft size={16} /> Tous les articles
          </Link>
          <h1 className="text-4xl sm:text-5xl font-serif font-light leading-tight text-sand-900">{post.title}</h1>
          {post.cover_image && (
            <img
              src={post.cover_image}
              alt={post.title}
              loading="eager"
              className="w-full rounded-2xl mt-10 aspect-[16/9] object-cover"
            />
          )}
          <div className="mt-10 prose-mystic text-lg">
            <ReactMarkdown
              components={{
                h1: ({ node, ...p }) => <h2 className="text-3xl font-serif text-sand-900 mt-10 mb-4" {...p} />,
                h2: ({ node, ...p }) => <h2 className="text-3xl font-serif text-sand-900 mt-10 mb-4" {...p} />,
                h3: ({ node, ...p }) => <h3 className="text-2xl font-serif text-sand-900 mt-8 mb-3" {...p} />,
                h4: ({ node, ...p }) => <h3 className="text-xl font-serif text-sand-900 mt-8 mb-3" {...p} />,
                p: ({ node, ...p }) => <p className="text-sand-700 leading-relaxed mb-5" {...p} />,
                ul: ({ node, ...p }) => <ul className="list-disc pl-6 space-y-2 text-sand-700 mb-5" {...p} />,
                ol: ({ node, ...p }) => <ol className="list-decimal pl-6 space-y-2 text-sand-700 mb-5" {...p} />,
                li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                strong: ({ node, ...p }) => <strong className="text-sand-900 font-medium" {...p} />,
                em: ({ node, ...p }) => <em className="italic text-sand-700" {...p} />,
                blockquote: ({ node, ...p }) => (
                  <blockquote className="border-l-4 border-[#B07A45] pl-6 my-8 italic font-serif text-xl text-sand-800" {...p} />
                ),
                a: ({ node, ...p }) => <a className="text-[#8B5E34] underline hover:text-sand-900" {...p} />,
                hr: () => <hr className="my-10 border-sand-300" />,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* CTA après l'article */}
          <div className="mt-16 rounded-2xl bg-sand-200/40 border border-sand-300/60 p-8 text-center">
            <p className="font-serif text-2xl text-sand-900 leading-snug">
              Envie d'explorer ce chemin avec un accompagnement personnalisé&nbsp;?
            </p>
            <p className="mt-3 text-sand-700">
              Je vous reçois en hypnose régressive et spirituelle,
              en visio ou à proximité du métro Église de Pantin.
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              <Link to="/hypnose" data-testid="article-cta-hypnose" className="btn-primary">
                Découvrir l'accompagnement <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="article-cta-contact" className="btn-outline">
                Me contacter
              </Link>
            </div>
          </div>

          {/* Articles similaires */}
          {related.length > 0 && (
            <section className="mt-20" data-testid="related-articles">
              <h2 className="text-2xl font-serif text-sand-900 mb-8">À lire aussi</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {related.map((p, i) => (
                  <Link
                    to={`/blog/${p.slug}`}
                    key={p.id}
                    data-testid={`related-${i}`}
                    className="group block rounded-2xl overflow-hidden bg-white/70 border border-sand-300/60 transition-all hover:-translate-y-1 hover:shadow-md"
                  >
                    {p.cover_image && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="font-serif text-lg text-sand-900 leading-snug">{p.title}</h3>
                      <p className="mt-2 text-sm text-sand-700 line-clamp-2">{p.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}
